<script setup>
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { ChatBubbleLeftRightIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import {
  useDistributionStore,
  useParticipantTimepointStore,
  useReviewStore,
} from "@/stores";
import { reviewService } from "@/api";

const { locale, t } = useI18n();
const reviewStore = useReviewStore();
const distributionStore = useDistributionStore();
const participantTimepointStore = useParticipantTimepointStore();
const { context, enabled, expired } = storeToRefs(reviewStore);

const open = ref(false);
const loading = ref(false);
const saving = ref(false);
const body = ref("");
const comments = ref([]);
const error = ref(null);

const contextLabel = computed(() => {
  if (!context.value) return "";
  const key = `review.contexts.${context.value.type}`;
  return t(key);
});

const handleError = (failure) => {
  if (
    failure?.response?.status === 403 &&
    failure?.data?.code === "review_session_unavailable"
  ) {
    reviewStore.markExpired();
    error.value = t("review.expired");
  } else {
    error.value = failure?.data?.message ?? t("review.error");
  }
};

const load = async () => {
  if (!context.value || !enabled.value) return;
  loading.value = true;
  error.value = null;
  try {
    const response = await reviewService.fetchComments(
      distributionStore.distribution_id,
      participantTimepointStore.participant_timepoint_id,
      context.value,
    );
    comments.value = response.comments ?? [];
  } catch (failure) {
    handleError(failure);
  } finally {
    loading.value = false;
  }
};

const toggle = async () => {
  open.value = !open.value;
  if (open.value) await load();
};

const submit = async () => {
  if (!body.value.trim() || saving.value || !enabled.value) return;
  saving.value = true;
  error.value = null;
  try {
    await reviewService.createComment(
      distributionStore.distribution_id,
      participantTimepointStore.participant_timepoint_id,
      context.value,
      locale.value,
      body.value,
    );
    body.value = "";
    await load();
  } catch (failure) {
    handleError(failure);
  } finally {
    saving.value = false;
  }
};

const formatDate = (value) =>
  new Intl.DateTimeFormat(locale.value, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));

watch(context, () => {
  comments.value = [];
  body.value = "";
  error.value = null;
  if (open.value) load();
});
</script>

<template>
  <button
    v-if="reviewStore.mode?.enabled"
    type="button"
    class="fixed right-4 bottom-16 cursor-pointer z-40 inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-700 text-white shadow-lg hover:bg-teal-800 print:hidden"
    :title="$t('review.title')"
    @click="toggle"
  >
    <ChatBubbleLeftRightIcon class="h-6 w-6" />
    <span class="sr-only">{{ $t("review.title") }}</span>
  </button>

  <div v-if="open" class="fixed inset-0 z-50 print:hidden">
    <button
      type="button"
      class="absolute inset-0 bg-black/30"
      :aria-label="$t('review.close')"
      @click="open = false"
    ></button>
    <aside
      class="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-xl dark:bg-gray-900"
    >
      <header
        class="flex items-start justify-between border-b border-gray-200 p-5 dark:border-gray-700"
      >
        <div>
          <h2 class="text-lg font-semibold">{{ $t("review.title") }}</h2>
          <p class="mt-1 text-sm text-gray-500">{{ contextLabel }}</p>
        </div>
        <button
          type="button"
          class="p-1 text-gray-500"
          :title="$t('review.close')"
          @click="open = false"
        >
          <XMarkIcon class="h-6 w-6" />
        </button>
      </header>

      <div class="flex-1 overflow-y-auto p-5">
        <p
          v-if="expired"
          class="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          {{ $t("review.expired") }}
        </p>
        <p v-if="error && !expired" class="mb-4 text-sm text-red-600">
          {{ error }}
        </p>
        <p v-if="loading" class="py-8 text-center text-gray-500">...</p>
        <p
          v-else-if="comments.length === 0"
          class="py-8 text-center text-sm text-gray-500"
        >
          {{ $t("review.empty") }}
        </p>
        <ol v-else class="divide-y divide-gray-200 dark:divide-gray-700">
          <li v-for="comment in comments" :key="comment.id" class="py-4">
            <div
              class="flex flex-wrap items-center gap-2 text-xs text-gray-500"
            >
              <strong class="text-gray-800 dark:text-gray-100">{{
                comment.author.name
              }}</strong>
              <span>{{ formatDate(comment.created_at) }}</span>
              <span
                v-if="comment.status"
                class="rounded bg-gray-100 px-2 py-0.5 dark:bg-gray-700"
                >{{ $t(`review.${comment.status}`) }}</span
              >
            </div>
            <p class="mt-2 whitespace-pre-wrap break-words text-sm">
              {{ comment.body }}
            </p>
          </li>
        </ol>
      </div>

      <form
        class="border-t border-gray-200 p-5 dark:border-gray-700"
        @submit.prevent="submit"
      >
        <textarea
          v-model="body"
          rows="4"
          maxlength="5000"
          class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800"
          :placeholder="$t('review.placeholder')"
          :disabled="!enabled || !context"
        ></textarea>
        <button
          type="submit"
          class="mt-3 w-full rounded-md bg-teal-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          :disabled="saving || !body.trim() || !enabled || !context"
        >
          {{ $t("review.send") }}
        </button>
      </form>
    </aside>
  </div>
</template>
